import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Separator,
  Stack,
} from "@altimateai/lego";
import { useState, useMemo, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useAppState } from "@/modules/app/useAppContext";
import { useTemplatesV2 } from "./services/templateApi";
import type { TemplateSelectionModalProps } from "./types";
import type { SubscriptionV2Template } from "@/pages/subscriptions/services/types";
import {
  type Platform,
  GROUP_ICON_MAP,
  GROUP_CUSTOM_ICON_MAP,
  DEFAULT_GROUP_ICON,
  PLATFORMS,
} from "./templateSelectionConstants";
import {
  filterTemplatesByPlatform,
  getTemplateSourceInfoMap,
  getTemplateSources,
} from "./templateSelectionHelpers";

const TemplateSelectionModal = ({
  open,
  onClose,
  onSelectTemplate,
  onContinueWithoutTemplate,
}: TemplateSelectionModalProps) => {
  const { isDatabricksTenant } = useAppState();
  const tenantPlatform: Platform = isDatabricksTenant ? "databricks" : "snowflake";
  const tenantPlatformConfig = PLATFORMS.find(p => p.id === tenantPlatform) ?? PLATFORMS[0];
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>(tenantPlatform);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SubscriptionV2Template | null>(null);

  const { data: templatesData, isLoading, isError } = useTemplatesV2();
  const templateItems = templatesData?.items;

  const platformTemplates = useMemo(() => {
    return filterTemplatesByPlatform(templateItems ?? [], selectedPlatform);
  }, [templateItems, selectedPlatform]);

  const sources = useMemo(() => getTemplateSources(platformTemplates), [platformTemplates]);
  const sourceInfoMap = useMemo(
    () => getTemplateSourceInfoMap(platformTemplates),
    [platformTemplates]
  );

  // Keep platform tab in sync with tenant type (handles tenant switches).
  useEffect(() => {
    setSelectedPlatform(tenantPlatform);
  }, [tenantPlatform]);

  // Auto-select first source when sources change
  useEffect(() => {
    if (sources.length > 0 && (selectedSource === null || !sources.includes(selectedSource))) {
      setSelectedSource(sources[0]);
    }
  }, [sources, selectedSource]);

  // Templates for the selected group
  const filteredTemplates = useMemo(() => {
    if (!selectedSource) return [];
    return platformTemplates.filter(t => t.group?.key === selectedSource);
  }, [platformTemplates, selectedSource]);

  const handlePlatformChange = (platform: Platform) => {
    setSelectedPlatform(platform);
    setSelectedSource(null);
    setSelectedTemplate(null);
  };

  const handleSourceChange = (source: string) => {
    setSelectedSource(source);
    setSelectedTemplate(null);
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
    }
  };
  const TenantPlatformIcon = tenantPlatformConfig.Icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!al-max-w-6xl al-p-0">
        <DialogTitle className="al-text-2xl al-font-bold">Start with a template</DialogTitle>
        <Separator className="al-w-full" />

        <Stack direction="row" className="al-h-[calc(90vh-200px)] al-overflow-hidden">
          {/* Left sidebar */}
          <Stack direction="column" gap="md" className="al-w-[260px] al-flex-shrink-0 al-pr-2">
            <Stack
              direction="row"
              className="al-rounded-lg al-border al-border-gray-200 al-overflow-hidden"
            >
              <Button
                aria-label={`Select ${tenantPlatformConfig.label} platform`}
                variant={selectedPlatform === tenantPlatformConfig.id ? "default" : "outline"}
                onClick={() => handlePlatformChange(tenantPlatformConfig.id)}
                className="al-flex-1 al-rounded-none al-py-2 al-text-xs al-font-medium"
              >
                <TenantPlatformIcon size={14} />
                {tenantPlatformConfig.label}
              </Button>
            </Stack>

            {/* Group list */}
            <ul className="al-flex-1 al-overflow-y-auto al-p-0 al-space-y-0.5">
              {isLoading ? (
                <Typography variant="body" className="al-text-gray-500 al-text-sm al-px-3">
                  Loading...
                </Typography>
              ) : isError ? (
                <Typography variant="body" className="al-text-red-500 al-text-sm al-px-3">
                  Failed to load sources
                </Typography>
              ) : sources.length === 0 ? (
                <Typography variant="body" className="al-text-gray-500 al-text-sm al-px-3">
                  No sources found
                </Typography>
              ) : (
                sources.map(source => {
                  const info = sourceInfoMap[source];
                  const customIcon = GROUP_CUSTOM_ICON_MAP[info?.icon];
                  const GroupIcon = GROUP_ICON_MAP[info?.icon] ?? DEFAULT_GROUP_ICON;
                  const isActive = selectedSource === source;
                  return (
                    <li key={source} className="al-list-none">
                      <Button
                        variant={isActive ? "secondary" : "ghost"}
                        onClick={() => handleSourceChange(source)}
                        className="al-w-full !al-justify-start al-px-3 al-py-2 al-rounded-md"
                      >
                        <Stack direction="row" justify="start" gap="sm" align="center">
                          {customIcon ?? (
                            <GroupIcon
                              size={16}
                              className={cn(isActive ? "al-text-blue-600" : "al-text-gray-400")}
                            />
                          )}
                          <span className="al-text-sm">{info?.label ?? source}</span>
                        </Stack>
                      </Button>
                    </li>
                  );
                })
              )}
            </ul>
          </Stack>

          {/* Right side - templates grid */}
          <Stack direction="column" className="al-flex-1 al-min-w-0 al-overflow-hidden">
            {/* Group header */}
            {selectedSource && sourceInfoMap[selectedSource] && (
              <div className="al-px-4 al-pb-2 al-border-b al-border-gray-100">
                <Typography variant="body" weight="medium" className="al-text-gray-800">
                  {sourceInfoMap[selectedSource].label}
                </Typography>
                <Typography variant="body" size="xs" className="al-text-gray-500">
                  {filteredTemplates.length} template{filteredTemplates.length !== 1 ? "s" : ""}
                </Typography>
              </div>
            )}

            <div className="al-px-4 al-overflow-y-auto al-flex-1 al-pt-3">
              {isLoading ? (
                <div className="al-text-center al-py-12">
                  <Typography variant="body" className="al-text-gray-500">
                    Loading templates...
                  </Typography>
                </div>
              ) : isError ? (
                <div className="al-text-center al-py-12">
                  <Typography variant="body" className="al-text-red-500">
                    Failed to load templates. Please try again.
                  </Typography>
                </div>
              ) : filteredTemplates.length > 0 ? (
                <div className="al-grid al-grid-cols-1 md:al-grid-cols-2 al-gap-4">
                  {filteredTemplates.map(template => {
                    const isSelected = selectedTemplate?.id === template.id;
                    return (
                      <Button
                        key={template.id}
                        variant="ghost"
                        onClick={() => setSelectedTemplate(template)}
                        className={cn(
                          "!al-h-auto !al-justify-start al-text-left al-p-4 al-border al-rounded-lg al-transition-all",
                          isSelected
                            ? "al-border-blue-500 al-bg-blue-50"
                            : "al-border-gray-200 hover:al-border-blue-400"
                        )}
                      >
                        <Stack
                          direction="column"
                          align="start"
                          justify="start"
                          className="al-h-full"
                        >
                          <Typography
                            variant="body"
                            weight="medium"
                            className="al-mb-1 al-text-gray-900"
                          >
                            {template.name}
                          </Typography>
                          <Typography
                            variant="body"
                            size="xs"
                            className="al-text-gray-500 al-line-clamp-2 al-text-wrap"
                          >
                            {template.description}
                          </Typography>
                        </Stack>
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <div className="al-text-center al-py-12">
                  <Typography variant="body" className="al-text-gray-500">
                    No templates available
                  </Typography>
                </div>
              )}
            </div>
          </Stack>
        </Stack>

        {/* Footer */}
        <Stack direction="row" justify="end" gap="md" className="al-py-4 al-border-t">
          <Button variant="outline" onClick={onContinueWithoutTemplate}>
            Continue without template
          </Button>
          <Button onClick={handleUseTemplate} disabled={!selectedTemplate}>
            Use this template
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelectionModal;
